import React from "react";
import { Box, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  DetailHeader,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../ui/detailView";
import { StatusChip } from "../ui/statusChip/statusChip.component";
import { formatUserName } from "../../utils";
import { PermissionGroup } from "../../types/permissions";

type UserAccount = {
  first_name: string;
  last_name: string;
  email: string;
  groups?: PermissionGroup[];
  group_names?: string;
  is_active?: boolean;
};

interface UserAccountDetailProps {
  user: UserAccount;
  /** Sections shown after the account section, e.g. the user's consents. */
  children?: React.ReactNode;
}

/** A person's account page - used for both the own profile and a workspace user. */
export const UserAccountDetail = ({ user, children }: UserAccountDetailProps) => {
  const { t } = useTranslation();
  const name = formatUserName(user);
  const groupNames = user.groups?.length
    ? user.groups.map((group) => group.name)
    : (user.group_names || "").split(",").map((group) => group.trim()).filter(Boolean);

  return (
    <DetailPage>
      <DetailHeader
        avatarText={name}
        title={name}
        subtitle={user.email !== name ? user.email : undefined}
        chips={
          user.is_active !== undefined && (
            <StatusChip
              color={user.is_active ? "success" : "warning"}
              label={user.is_active ? t("Active") : t("Inactive")}
            />
          )
        }
      />

      <DetailSection title={t("Account")}>
        <DetailRow label={t("First Name")} value={user.first_name} />
        <DetailRow label={t("Last Name")} value={user.last_name} />
        <DetailRow label={t("Email")} value={user.email} />
        <DetailRow label={t("Permission Groups")}>
          {groupNames.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {groupNames.map((groupName) => (
                <Chip key={groupName} label={groupName} size="small" variant="outlined" />
              ))}
            </Box>
          )}
        </DetailRow>
      </DetailSection>

      {children}
    </DetailPage>
  );
};
